import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {formatPrice} from '../../../../utils/currencyUtils';

interface InvoicePreviewProps {
    productName: string;
    quantity: number;
    purchasePrice: number;
    supportPrice: number;
    currency: string;
    pricingType: string;
    licenseDurationLabel?: string;
    packageSize?: number;
}

export function InvoicePreview({
                                   productName,
                                   quantity,
                                   purchasePrice,
                                   supportPrice,
                                   currency,
                                   pricingType,
                                   licenseDurationLabel,
                                   packageSize
                               }: InvoicePreviewProps) {
    const {t} = useTranslationWithKeys();

    const formatCurrency = (amount: number): string => {
        return formatPrice(amount / 100, currency);
    };

    const renderMonthlyFirstYear = () => (
        <>
            <div className="border-t my-4"></div>
            <div className="space-y-2">
                <div className="font-semibold">{t('invoice.preview.firstYear')}</div>
                <div className="flex justify-between text-sm">
                    <span>{t('invoice.preview.productPurchase')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>{t('invoice.preview.total')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {t('invoice.preview.excludingVat')}
                </div>
            </div>
            <div className="border-t my-4"></div>
            <div className="space-y-2">
                <div className="font-semibold">{t('invoice.preview.subsequentYears')}</div>
                <div className="flex justify-between text-sm">
                    <span>{t('invoice.preview.smaSubscription')}</span>
                    <span>{formatCurrency(supportPrice)}/year</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>{t('invoice.preview.total')}</span>
                    <span>{formatCurrency(supportPrice)}/year</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {t('invoice.preview.excludingVat')}
                </div>
            </div>
        </>
    );

    const renderMonthly6Month = () => (
        <>
            <div className="border-t my-4"></div>
            <div className="space-y-2">
                <div className="flex justify-between text-sm">
                    <span>{t('invoice.preview.productPurchase')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>{t('invoice.preview.total')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {t('invoice.preview.excludingVat')}
                </div>
            </div>
        </>
    );

    const renderPackage = () => (
        <>
            <div className="border-t my-4"></div>
            <div className="space-y-2">
                <div className="flex justify-between text-sm">
                    <span>{t('invoice.preview.productPurchase')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>{t('invoice.preview.total')}</span>
                    <span>{formatCurrency(purchasePrice)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    {t('invoice.preview.excludingVat')}
                </div>
            </div>
        </>
    );

    return (
        <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">{t('invoice.preview.title')}</h3>
            <div className="border-t my-4"></div>
            <div className="space-y-2">
                <div className="flex justify-between">
                    <span className="font-medium">{t('invoice.preview.product')}</span>
                    <span>{productName}</span>
                </div>
                <div className="flex justify-between">
                    <span className="font-medium">{t('invoice.preview.quantity')}</span>
                    <span>{t('pricing.quantityOf', {count: quantity})}</span>
                </div>
            </div>
            {pricingType === 'monthly_first_year' && renderMonthlyFirstYear()}
            {pricingType === 'monthly_6month' && renderMonthly6Month()}
            {pricingType === 'package' && renderPackage()}
        </div>
    );
}
